import consola from 'consola'

declare const batchSDK: (...args: unknown[]) => void

export type BatchEventAttributes = Record<string, string | URL>

// Typage pour la file d'attente globale (définie dans index.php)
declare global {
  interface Window {
    runAfterBatchReady?: (callback: () => void) => void
  }
}

/**
 * Helper pour envoyer des événements au SDK Batch.
 *
 * Utilise `window.runAfterBatchReady` pour mettre en file d'attente
 * les events quand le SDK n'est pas encore chargé (mode Didomi).
 *
 * Events implémentés (cf. spreadsheet Batch x Public Sénat) :
 * - visited_article : visite d'une page article
 * - read_article    : lecture d'un article (scroll >= 90 %)
 * - started_content : début de lecture d'un contenu vidéo/podcast
 * - finished_content: fin de lecture d'un contenu vidéo/podcast
 * - subscribed_nl   : inscription à une newsletter (géré dans NewsletterForm.ts)
 */
/** Clés d'attributs dont la valeur doit être castée en objet URL pour le SDK Batch. */
const URL_ATTRIBUTE_KEYS = ['url', 'image']

class BatchTrackingHelper {
  private static readEventSent = false

  /**
   * Vérifie que le système Batch est activé (file d'attente disponible).
   */
  private static isEnabled(): boolean {
    return typeof window.runAfterBatchReady === 'function'
  }

  /**
   * Convertit les champs URL (url, image) de string vers objet URL
   * pour que le SDK Batch les reconnaisse comme type URL et non string.
   *
   * @see https://doc.batch.com/developer/sdk/web/profile-data/events
   */
  private static castUrlAttributes(attributes: BatchEventAttributes): BatchEventAttributes {
    const result: BatchEventAttributes = {}

    for (const key of Object.keys(attributes)) {
      const value = attributes[key]
      if (URL_ATTRIBUTE_KEYS.includes(key) && typeof value === 'string' && value.length > 0) {
        try {
          result[key] = new URL(value)
        } catch {
          result[key] = value
        }
      } else {
        result[key] = value
      }
    }

    return result
  }

  /**
   * Envoie un événement au SDK Batch via la file d'attente.
   * Si le SDK est déjà dispo, l'event part immédiatement.
   * Sinon il est empilé et sera envoyé après le consentement Didomi.
   *
   * Les attributs `url` et `image` sont automatiquement castés en objet URL.
   */
  static trackEvent(eventName: string, attributes?: BatchEventAttributes): void {
    if (!this.isEnabled()) {
      return
    }

    const castAttributes = attributes ? this.castUrlAttributes(attributes) : {}

    consola.trace(`[BatchTracking] ${eventName}`, castAttributes)

    window.runAfterBatchReady!(() => {
      batchSDK((api: { trackEvent: (name: string, opts?: { attributes?: BatchEventAttributes }) => void }) => {
        api.trackEvent(eventName, {
          attributes: castAttributes,
        })
      })
    })
  }

  /**
   * Récupère les données Batch de la page courante depuis le data-attribute sur le body.
   */
  static getPageData(): BatchEventAttributes | null {
    const raw = document.body.dataset.batchTracking
    if (!raw) {
      return null
    }

    try {
      return JSON.parse(raw) as BatchEventAttributes
    } catch {
      return null
    }
  }

  /**
   * Initialise le tracking Batch pour la page courante.
   * Envoie `visited_article` au chargement de la page article.
   */
  static init(): void {
    const data = this.getPageData()
    if (!data) {
      return
    }

    // Event: visited_article — au chargement de la page
    this.trackEvent('visited_article', data)

    consola.trace('[BatchTracking] initialized', data)
  }

  /**
   * Envoie l'event `read_article` (une seule fois par page).
   * Appelé par TrackingHelper quand le scroll atteint 90%.
   */
  static trackReadArticle(): void {
    if (this.readEventSent) {
      return
    }

    const data = this.getPageData()
    if (!data) {
      return
    }

    this.readEventSent = true
    this.trackEvent('read_article', data)
  }

  /**
   * Envoie l'event `started_content` quand un contenu vidéo/podcast démarre.
   */
  static trackStartedContent(attributes: BatchEventAttributes): void {
    this.trackEvent('started_content', attributes)
  }

  /**
   * Envoie l'event `finished_content` quand un contenu vidéo/podcast se termine.
   */
  static trackFinishedContent(attributes: BatchEventAttributes): void {
    this.trackEvent('finished_content', attributes)
  }
}

export default BatchTrackingHelper
